import React, { useMemo, useState, useRef } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip';
import { AlertTriangle, CheckCircle2, XCircle, AlertCircle, Trash2, Download, Upload, Move } from 'lucide-react';
import { FOS_TARGETS, getFOSStatus, getFOSColor } from '@/lib/calculations';

export const OverviewTab = ({ 
  piles, 
  activePileId, 
  onSelectPile, 
  onDeletePile,
  onUpdatePosition,
  onExportCSV,
  onImportCSV 
}) => {
  const [draggedPile, setDraggedPile] = useState(null);
  const [dragOffset, setDragOffset] = useState({ x: 0, y: 0 });
  const svgRef = useRef(null);
  const fileInputRef = useRef(null);

  // Calculate worst FS for each load case across all piles
  const worstFS = useMemo(() => {
    const result = {
      compOperating: { value: Infinity, pileId: null, pileName: '', caseId: '' },
      compStorm: { value: Infinity, pileId: null, pileName: '', caseId: '' },
      tensStorm: { value: Infinity, pileId: null, pileName: '', caseId: '' }
    };

    piles.forEach(pile => {
      if (!pile.fos) return;

      if (pile.fos.compOperating !== null && pile.fos.compOperating < result.compOperating.value) {
        result.compOperating = {
          value: pile.fos.compOperating,
          pileId: pile.id,
          pileName: pile.name,
          caseId: pile.designLoads?.compOperatingCaseId || ''
        };
      }

      if (pile.fos.compStorm !== null && pile.fos.compStorm < result.compStorm.value) {
        result.compStorm = {
          value: pile.fos.compStorm,
          pileId: pile.id,
          pileName: pile.name,
          caseId: pile.designLoads?.compStormCaseId || ''
        };
      }

      if (pile.fos.tensStorm !== null && pile.fos.tensStorm < result.tensStorm.value) {
        result.tensStorm = {
          value: pile.fos.tensStorm,
          pileId: pile.id,
          pileName: pile.name,
          caseId: pile.designLoads?.tensStormCaseId || ''
        };
      }
    });

    return result;
  }, [piles]);

  // Get color class based on FS and target
  const getColorClass = (fs, target) => {
    if (fs === Infinity || fs === null) return 'text-muted-foreground bg-muted';
    if (fs >= target) return 'text-green-700 bg-green-100 dark:text-green-400 dark:bg-green-900/30';
    return 'text-red-700 bg-red-100 dark:text-red-400 dark:bg-red-900/30';
  };

  // Get status badge
  const getStatusBadge = (minFS) => {
    const status = getFOSStatus(minFS);
    if (!status) return <Badge variant="outline">N/A</Badge>;
    
    const variants = {
      PASS: { variant: 'default', className: 'bg-green-500 hover:bg-green-600', icon: CheckCircle2 },
      WARNING: { variant: 'default', className: 'bg-amber-500 hover:bg-amber-600', icon: AlertCircle },
      FAIL: { variant: 'destructive', className: '', icon: XCircle }
    };
    
    const config = variants[status];
    const Icon = config.icon;
    
    return (
      <Badge variant={config.variant} className={config.className}>
        <Icon className="h-3 w-3 mr-1" />
        {status}
      </Badge>
    );
  };

  // Get pile circle color
  const getPileColor = (minFS) => {
    const color = getFOSColor(minFS);
    const colors = {
      green: 'fill-green-500 stroke-green-700',
      amber: 'fill-amber-500 stroke-amber-700',
      red: 'fill-red-500 stroke-red-700',
      gray: 'fill-gray-400 stroke-gray-600'
    };
    return colors[color] || colors.gray;
  };

  // Drag and drop handlers
  const handleMouseDown = (e, pile) => {
    if (!svgRef.current) return;
    
    const svgRect = svgRef.current.getBoundingClientRect();
    const viewBox = svgRef.current.viewBox.baseVal;
    const scaleX = viewBox.width / svgRect.width;
    const scaleY = viewBox.height / svgRect.height;
    
    const mouseX = (e.clientX - svgRect.left) * scaleX;
    const mouseY = (e.clientY - svgRect.top) * scaleY;
    
    setDraggedPile(pile.id);
    setDragOffset({
      x: mouseX - (pile.position.x + 25),
      y: mouseY - (pile.position.y + 25)
    });
    
    e.preventDefault();
  };

  const handleMouseMove = (e) => {
    if (!draggedPile || !svgRef.current) return;
    
    const svgRect = svgRef.current.getBoundingClientRect();
    const viewBox = svgRef.current.viewBox.baseVal;
    const scaleX = viewBox.width / svgRect.width;
    const scaleY = viewBox.height / svgRect.height;
    
    const mouseX = (e.clientX - svgRect.left) * scaleX;
    const mouseY = (e.clientY - svgRect.top) * scaleY;
    
    const newX = Math.max(0, Math.min(300, mouseX - dragOffset.x - 25));
    const newY = Math.max(0, Math.min(200, mouseY - dragOffset.y - 25));
    
    if (onUpdatePosition) {
      onUpdatePosition(draggedPile, { x: newX, y: newY });
    }
  };

  const handleMouseUp = () => {
    setDraggedPile(null);
  };

  // File import handler
  const handleFileImport = (e) => {
    const file = e.target.files?.[0];
    if (file && onImportCSV) {
      onImportCSV(file);
    }
    e.target.value = '';
  };

  return (
    <div className="space-y-6" data-testid="overview-tab">
      {/* Import/Export Buttons */}
      <div className="flex justify-end gap-2">
        <input
          ref={fileInputRef}
          type="file"
          accept=".csv"
          onChange={handleFileImport}
          className="hidden"
          data-testid="import-file-input"
        />
        <Button
          variant="outline"
          size="sm"
          onClick={() => fileInputRef.current?.click()}
          data-testid="import-csv-btn"
        >
          <Upload className="h-4 w-4 mr-2" />
          Import CSV
        </Button>
        <Button
          variant="outline"
          size="sm"
          onClick={onExportCSV}
          data-testid="export-csv-btn"
        >
          <Download className="h-4 w-4 mr-2" />
          Export CSV
        </Button>
      </div>

      {/* API FOS Summary Cards */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        {/* Operating Compression */}
        <Card className={getColorClass(worstFS.compOperating.value, FOS_TARGETS.compOperating)}>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium">
              Worst FS – Operating Compression
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="flex items-center justify-between">
              <div>
                <div className="text-3xl font-bold font-mono">
                  {worstFS.compOperating.value === Infinity ? '—' : worstFS.compOperating.value.toFixed(2)}
                </div>
                <div className="text-xs mt-1">Target ≥ {FOS_TARGETS.compOperating.toFixed(1)}</div>
              </div>
              {worstFS.compOperating.pileId && (
                <div className="text-right text-sm">
                  <div className="font-medium">{worstFS.compOperating.pileName}</div>
                  {worstFS.compOperating.caseId && (
                    <div className="text-xs opacity-70">{worstFS.compOperating.caseId}</div>
                  )}
                </div>
              )}
            </div>
            <div className="mt-2">
              {worstFS.compOperating.value >= FOS_TARGETS.compOperating ? (
                <Badge variant="default" className="bg-green-600"><CheckCircle2 className="h-3 w-3 mr-1" />PASS</Badge>
              ) : worstFS.compOperating.value === Infinity ? (
                <Badge variant="outline">No Data</Badge>
              ) : (
                <Badge variant="destructive"><XCircle className="h-3 w-3 mr-1" />FAIL</Badge>
              )}
            </div>
          </CardContent>
        </Card>

        {/* Storm Compression */}
        <Card className={getColorClass(worstFS.compStorm.value, FOS_TARGETS.compStorm)}>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium">
              Worst FS – Storm Compression
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="flex items-center justify-between">
              <div>
                <div className="text-3xl font-bold font-mono">
                  {worstFS.compStorm.value === Infinity ? '—' : worstFS.compStorm.value.toFixed(2)}
                </div>
                <div className="text-xs mt-1">Target ≥ {FOS_TARGETS.compStorm.toFixed(1)}</div>
              </div>
              {worstFS.compStorm.pileId && (
                <div className="text-right text-sm">
                  <div className="font-medium">{worstFS.compStorm.pileName}</div>
                  {worstFS.compStorm.caseId && (
                    <div className="text-xs opacity-70">{worstFS.compStorm.caseId}</div>
                  )}
                </div>
              )}
            </div>
            <div className="mt-2">
              {worstFS.compStorm.value >= FOS_TARGETS.compStorm ? (
                <Badge variant="default" className="bg-green-600"><CheckCircle2 className="h-3 w-3 mr-1" />PASS</Badge>
              ) : worstFS.compStorm.value === Infinity ? (
                <Badge variant="outline">No Data</Badge>
              ) : (
                <Badge variant="destructive"><XCircle className="h-3 w-3 mr-1" />FAIL</Badge>
              )}
            </div>
          </CardContent>
        </Card>

        {/* Storm Tension */}
        <Card className={getColorClass(worstFS.tensStorm.value, FOS_TARGETS.tensStorm)}>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium">
              Worst FS – Storm Tension
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="flex items-center justify-between">
              <div>
                <div className="text-3xl font-bold font-mono">
                  {worstFS.tensStorm.value === Infinity ? '—' : worstFS.tensStorm.value.toFixed(2)}
                </div>
                <div className="text-xs mt-1">Target ≥ {FOS_TARGETS.tensStorm.toFixed(1)}</div>
              </div>
              {worstFS.tensStorm.pileId && (
                <div className="text-right text-sm">
                  <div className="font-medium">{worstFS.tensStorm.pileName}</div>
                  {worstFS.tensStorm.caseId && (
                    <div className="text-xs opacity-70">{worstFS.tensStorm.caseId}</div>
                  )}
                </div>
              )}
            </div>
            <div className="mt-2">
              {worstFS.tensStorm.value >= FOS_TARGETS.tensStorm ? (
                <Badge variant="default" className="bg-green-600"><CheckCircle2 className="h-3 w-3 mr-1" />PASS</Badge>
              ) : worstFS.tensStorm.value === Infinity ? (
                <Badge variant="outline">No Data</Badge>
              ) : (
                <Badge variant="destructive"><XCircle className="h-3 w-3 mr-1" />FAIL</Badge>
              )}
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Plan View and Summary Table */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Plan View */}
        <Card>
          <CardHeader>
            <CardTitle className="text-lg flex items-center justify-between">
              <span>Pile Layout (Plan View)</span>
              <span className="text-xs font-normal text-muted-foreground flex items-center gap-1">
                <Move className="h-3 w-3" />
                Drag to reposition
              </span>
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="bg-muted/30 rounded-lg p-4 h-[300px] relative overflow-hidden">
              <svg 
                ref={svgRef}
                viewBox="0 0 350 250" 
                className="w-full h-full cursor-crosshair"
                data-testid="pile-layout-svg"
                onMouseMove={handleMouseMove}
                onMouseUp={handleMouseUp}
                onMouseLeave={handleMouseUp}
              >
                {/* Grid lines */}
                <defs>
                  <pattern id="grid" width="50" height="50" patternUnits="userSpaceOnUse">
                    <path d="M 50 0 L 0 0 0 50" fill="none" stroke="currentColor" strokeOpacity="0.1" strokeWidth="0.5"/>
                  </pattern>
                </defs>
                <rect width="100%" height="100%" fill="url(#grid)" />
                
                {/* Platform outline */}
                <rect 
                  x="20" y="20" 
                  width="310" height="210" 
                  fill="none" 
                  stroke="currentColor" 
                  strokeOpacity="0.3" 
                  strokeWidth="2"
                  strokeDasharray="5,5"
                />
                
                {/* Pile circles */}
                <TooltipProvider>
                  {piles.map(pile => {
                    const minFS = pile.fos?.minFS;
                    const colorClass = getPileColor(minFS);
                    const isActive = pile.id === activePileId;
                    const isDragging = pile.id === draggedPile;
                    
                    return (
                      <Tooltip key={pile.id}>
                        <TooltipTrigger asChild>
                          <g 
                            className={`cursor-grab transition-transform ${isDragging ? 'cursor-grabbing' : 'hover:scale-110'}`}
                            onMouseDown={(e) => handleMouseDown(e, pile)}
                            onClick={() => !isDragging && onSelectPile(pile.id)}
                            data-testid={`pile-circle-${pile.id}`}
                          >
                            <circle
                              cx={pile.position.x + 25}
                              cy={pile.position.y + 25}
                              r={isActive ? 28 : 25}
                              className={`${colorClass} ${isActive ? 'stroke-[3]' : 'stroke-2'} ${isDragging ? 'opacity-70' : ''}`}
                              fillOpacity={0.8}
                            />
                            <text
                              x={pile.position.x + 25}
                              y={pile.position.y + 30}
                              textAnchor="middle"
                              className="fill-white font-bold text-xs pointer-events-none"
                              style={{ fontSize: '10px' }}
                            >
                              {pile.name}
                            </text>
                          </g>
                        </TooltipTrigger>
                        <TooltipContent side="top" className="p-3">
                          <div className="space-y-1 text-sm">
                            <div className="font-bold">{pile.name}</div>
                            {pile.results && (
                              <>
                                <div>Q<sub>u</sub>: {pile.results.ultimateCapacity.toFixed(3)} MN</div>
                                <div>Q<sub>allow</sub>: {pile.results.allowableCapacity.toFixed(3)} MN</div>
                              </>
                            )}
                            {pile.fos?.minFS && (
                              <>
                                <div>Min FS: {pile.fos.minFS.toFixed(2)}</div>
                                <div className="text-xs text-muted-foreground">{pile.fos.governingCase}</div>
                              </>
                            )}
                            <div className="text-xs text-muted-foreground mt-2">
                              Position: ({pile.position.x.toFixed(0)}, {pile.position.y.toFixed(0)})
                            </div>
                          </div>
                        </TooltipContent>
                      </Tooltip>
                    );
                  })}
                </TooltipProvider>
              </svg>
              
              {/* Legend */}
              <div className="absolute bottom-2 right-2 flex items-center gap-3 text-xs bg-background/80 rounded px-2 py-1">
                <div className="flex items-center gap-1">
                  <div className="w-3 h-3 rounded-full bg-green-500" />
                  <span>FS ≥ 1.5</span>
                </div>
                <div className="flex items-center gap-1">
                  <div className="w-3 h-3 rounded-full bg-amber-500" />
                  <span>1.2 – 1.5</span>
                </div>
                <div className="flex items-center gap-1">
                  <div className="w-3 h-3 rounded-full bg-red-500" />
                  <span>&lt; 1.2</span>
                </div>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Summary Statistics */}
        <Card>
          <CardHeader>
            <CardTitle className="text-lg">Summary Statistics</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div className="bg-muted/50 rounded-lg p-4 text-center">
                  <div className="text-3xl font-bold text-[#00284F] dark:text-blue-400">
                    {piles.length}
                  </div>
                  <div className="text-sm text-muted-foreground">Total Piles</div>
                </div>
                <div className="bg-muted/50 rounded-lg p-4 text-center">
                  <div className="text-3xl font-bold text-green-600">
                    {piles.filter(p => getFOSStatus(p.fos?.minFS) === 'PASS').length}
                  </div>
                  <div className="text-sm text-muted-foreground">Passing (FS ≥ 1.5)</div>
                </div>
                <div className="bg-muted/50 rounded-lg p-4 text-center">
                  <div className="text-3xl font-bold text-amber-600">
                    {piles.filter(p => getFOSStatus(p.fos?.minFS) === 'WARNING').length}
                  </div>
                  <div className="text-sm text-muted-foreground">Warning (1.2 – 1.5)</div>
                </div>
                <div className="bg-muted/50 rounded-lg p-4 text-center">
                  <div className="text-3xl font-bold text-red-600">
                    {piles.filter(p => getFOSStatus(p.fos?.minFS) === 'FAIL').length}
                  </div>
                  <div className="text-sm text-muted-foreground">Failing (&lt; 1.2)</div>
                </div>
              </div>
              
              {/* Capacity range */}
              {piles.some(p => p.results) && (
                <div className="border-t pt-4 mt-4">
                  <div className="text-sm font-medium mb-2">Capacity Range</div>
                  <div className="grid grid-cols-2 gap-2 text-sm">
                    <div className="flex justify-between">
                      <span className="text-muted-foreground">Min Q<sub>u</sub>:</span>
                      <span className="font-mono">
                        {Math.min(...piles.filter(p => p.results).map(p => p.results.ultimateCapacity)).toFixed(3)} MN
                      </span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-muted-foreground">Max Q<sub>u</sub>:</span>
                      <span className="font-mono">
                        {Math.max(...piles.filter(p => p.results).map(p => p.results.ultimateCapacity)).toFixed(3)} MN
                      </span>
                    </div>
                  </div>
                </div>
              )}
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Pile Summary Table */}
      <Card>
        <CardHeader>
          <CardTitle className="text-lg">Pile Summary</CardTitle>
        </CardHeader>
        <CardContent>
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Pile ID</TableHead>
                <TableHead className="text-right">Q<sub>u</sub> (MN)</TableHead>
                <TableHead className="text-right">Q<sub>allow</sub> (MN)</TableHead>
                <TableHead className="text-right">Min FS</TableHead>
                <TableHead>Governing Limit State</TableHead>
                <TableHead>Governing Case</TableHead>
                <TableHead className="text-center">Status</TableHead>
                <TableHead className="w-[50px]"></TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {piles.map(pile => (
                <TableRow 
                  key={pile.id}
                  className={`cursor-pointer hover:bg-muted/50 ${pile.id === activePileId ? 'bg-[#00284F]/10' : ''}`}
                  onClick={() => onSelectPile(pile.id)}
                  data-testid={`pile-row-${pile.id}`}
                >
                  <TableCell className="font-medium">{pile.name}</TableCell>
                  <TableCell className="text-right font-mono">
                    {pile.results ? pile.results.ultimateCapacity.toFixed(3) : '—'}
                  </TableCell>
                  <TableCell className="text-right font-mono">
                    {pile.results ? pile.results.allowableCapacity.toFixed(3) : '—'}
                  </TableCell>
                  <TableCell className="text-right font-mono font-semibold">
                    {pile.fos?.minFS ? pile.fos.minFS.toFixed(2) : '—'}
                  </TableCell>
                  <TableCell className="text-sm">
                    {pile.results?.governingComponent || '—'}
                  </TableCell>
                  <TableCell className="text-sm">
                    {pile.fos?.governingCase || '—'}
                  </TableCell>
                  <TableCell className="text-center">
                    {getStatusBadge(pile.fos?.minFS)}
                  </TableCell>
                  <TableCell>
                    <Button
                      variant="ghost"
                      size="icon"
                      className="h-8 w-8 text-muted-foreground hover:text-destructive"
                      onClick={(e) => {
                        e.stopPropagation();
                        onDeletePile && onDeletePile(pile.id);
                      }}
                      disabled={piles.length <= 1}
                      data-testid={`delete-pile-btn-${pile.id}`}
                    >
                      <Trash2 className="h-4 w-4" />
                    </Button>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </CardContent>
      </Card>

      {/* No results warning */}
      {!piles.some(p => p.results) && (
        <Card className="border-amber-500/50 bg-amber-500/5">
          <CardContent className="pt-6">
            <div className="flex items-center gap-3">
              <AlertTriangle className="h-5 w-5 text-amber-500" />
              <div>
                <p className="font-medium text-amber-700 dark:text-amber-400">No Analysis Results</p>
                <p className="text-sm text-muted-foreground">
                  Click "Run All" to analyze all piles and populate the dashboard.
                </p>
              </div>
            </div>
          </CardContent>
        </Card>
      )}
    </div>
  );
};
